import { useState } from "react";
import { api, formatApiError, formatPhone, digitsOnly } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import { useLocations } from "@/lib/useLocations";
import { activeLocations } from "@/lib/locations";
import { CardSkeleton } from "@/components/Skeleton";
import { Building2, Check, Loader2, MapPin, Plus } from "lucide-react";

/** Settings → Locations.
 *
 *  Locations are deactivated, never deleted: appointments reference location_id forever,
 *  so a hard delete would leave history pointing at nothing. The backend refuses to close
 *  the last active site, and refuses to close one that still has upcoming visits — it
 *  returns the count, which this surfaces verbatim because the number is what tells staff
 *  how much rescheduling stands between them and closing the office.
 */

// Enough to cover a US clinic without pretending to be a timezone picker. Free text is
// still accepted; the backend validates against the real IANA database either way.
const COMMON_ZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
];

const BLANK = { name: "", address: "", phone: "", timezone: "", active: true };

function LocationForm({ initial, idPrefix, busy, onCancel, onSave, saveLabel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-3" data-testid={`location-form-${idPrefix}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block sm:col-span-2">
          <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Name</span>
          <input data-testid={`location-name-${idPrefix}`} value={form.name} onChange={set("name")}
                 placeholder="e.g. Northside Annex" className="linput mt-1.5" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Address</span>
          <input data-testid={`location-address-${idPrefix}`} value={form.address} onChange={set("address")}
                 placeholder="88 North Road, Suite 2" className="linput mt-1.5" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Phone</span>
          <input data-testid={`location-phone-${idPrefix}`} value={form.phone}
                 onChange={set("phone")} inputMode="tel" placeholder="(555) 555-5555"
                 onBlur={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                 className="linput mt-1.5" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Timezone</span>
          <input data-testid={`location-timezone-${idPrefix}`} value={form.timezone || ""}
                 onChange={set("timezone")} list="ros-timezones"
                 placeholder="Same as the clinic" className="linput mt-1.5" />
          <span className="block text-xs text-muted-foreground mt-1">
            Leave blank to follow the clinic's timezone. Only set this for a site in a
            different zone — every time this location shows is read in it.
          </span>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid={`location-save-${idPrefix}`} disabled={busy || !form.name.trim()}
                onClick={() => onSave({ ...form, phone: digitsOnly(form.phone) })}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {saveLabel}
        </button>
        <button onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground px-2">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function LocationSettings() {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const canEdit = ["owner", "system_admin"].includes(user?.role);
  const { locations, reload } = useLocations({ includeInactive: true });

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const open = activeLocations(locations || []);
  const isLast = (loc) => open.length <= 1 && loc.active !== false;

  async function create(form) {
    setBusy("new"); setError("");
    try {
      await api.post("/locations", form);
      setAdding(false); reload();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  }

  async function save(id, form) {
    setBusy(id); setError("");
    try {
      await api.put(`/locations/${id}`, form);
      setEditingId(""); reload();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  }

  async function setActive(loc, active) {
    setBusy(loc.id); setError("");
    try {
      await api.put(`/locations/${loc.id}`, {
        name: loc.name, address: loc.address || "", phone: loc.phone || "",
        timezone: loc.timezone || null, active,
      });
      reload();
    } catch (e) {
      // The backend's refusal is the message worth showing: it carries the count.
      setError(formatApiError(e.response?.data?.detail));
    }
    setBusy("");
  }

  return (
    <div className="space-y-6" data-testid="settings-locations">
      <style>{`.linput{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));border-radius:0.375rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.linput:focus{box-shadow:0 0 0 2px hsl(var(--ring))}`}</style>
      <datalist id="ros-timezones">
        {COMMON_ZONES.map((z) => <option key={z} value={z} />)}
      </datalist>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Locations</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Where you see patients. Provider hours, booking and the calendar are all
              set per location.
            </p>
          </div>
          {canEdit && !adding && (
            <button data-testid="location-add-button" onClick={() => { setAdding(true); setError(""); }}
                    className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-md px-3 py-2 hover:bg-primary/90">
              <Plus className="w-4 h-4" /> Add location
            </button>
          )}
        </div>

        {!canEdit && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
            Only the Clinic Owner can change locations. Viewing in read-only mode.
          </p>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4"
             data-testid="location-error">{error}</p>
        )}

        {adding && (
          <div className="border border-border rounded-lg p-4 mb-4">
            <LocationForm initial={BLANK} idPrefix="new" busy={busy === "new"}
                          saveLabel="Create location"
                          onCancel={() => { setAdding(false); setError(""); }}
                          onSave={create} />
          </div>
        )}

        {locations === null ? <CardSkeleton lines={3} /> : (
          <div className="divide-y divide-border" data-testid="location-list">
            {locations.map((loc) => {
              const inactive = loc.active === false;
              if (editingId === loc.id) {
                return (
                  <div key={loc.id} className="py-4">
                    <LocationForm
                      initial={{ name: loc.name, address: loc.address || "",
                                 phone: formatPhone(loc.phone), timezone: loc.timezone || "",
                                 active: loc.active !== false }}
                      idPrefix={loc.id} busy={busy === loc.id} saveLabel="Save"
                      onCancel={() => { setEditingId(""); setError(""); }}
                      onSave={(form) => save(loc.id, form)} />
                  </div>
                );
              }
              return (
                <div key={loc.id} data-testid={`location-row-${loc.id}`}
                     className={`flex flex-wrap items-start justify-between gap-3 py-3 ${inactive ? "opacity-60" : ""}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      {loc.name}
                      {inactive && (
                        <span className="text-xs text-muted-foreground" data-testid={`location-closed-${loc.id}`}>
                          · closed
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 ml-5">
                      {[loc.address, formatPhone(loc.phone)].filter(Boolean).join(" · ") || "No address on file"}
                    </p>
                    {loc.timezone && (
                      <p className="text-xs text-muted-foreground ml-5">
                        Times shown in {loc.timezone}
                      </p>
                    )}
                    {!loc.timezone && (
                      <p className="text-xs text-muted-foreground/70 ml-5">
                        Follows the clinic ({clinic?.timezone || "America/New_York"})
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`location-edit-${loc.id}`}
                              onClick={() => { setEditingId(loc.id); setError(""); }}
                              className="text-xs font-medium border border-border rounded-md px-2.5 py-1 hover:bg-secondary">
                        Edit
                      </button>
                      {inactive ? (
                        <button data-testid={`location-reopen-${loc.id}`} disabled={busy === loc.id}
                                onClick={() => setActive(loc, true)}
                                className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2.5 py-1 hover:bg-secondary disabled:opacity-60">
                          {busy === loc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Reopen
                        </button>
                      ) : (
                        <button data-testid={`location-close-${loc.id}`}
                                disabled={busy === loc.id || isLast(loc)}
                                title={isLast(loc) ? "A clinic needs at least one open location" : ""}
                                onClick={() => setActive(loc, false)}
                                className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2.5 py-1 hover:bg-secondary hover:border-destructive hover:text-destructive disabled:opacity-40 disabled:hover:border-border disabled:hover:text-current">
                          {busy === loc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                          Close
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border">
          Closing a location keeps its history. Past appointments still show where they
          happened; it simply stops appearing in booking and on the calendar.
        </p>
      </div>
    </div>
  );
}
